import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ConfigurationService } from '@ciam/library';
import { defaultIfEmpty, filter, map } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { WindowRef } from '../../../util/window.ref';
import { QueryParamService } from '../../services/query-param/query-param.service';
import { GdlService } from '../../services/gdl-service/gdl.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OptimDecision, OptimService } from '@ciam/library';

/**
 * Loads WLGN PIN page into an iframe for capturing user's elevated authority
 */
@Component({
  selector: 'wpro-two-factor',
  templateUrl: './two-factor.component.html',
  styleUrls: ['./two-factor.component.css'],
  standalone: false
})
export class TwoFactorComponent implements AfterViewInit, OnDestroy, OnInit {
  private twoFactorResponse$ = new Subject<boolean | null>();

  isLoading = false;
  isMock: boolean; // FIXME: mocks should not be internal to a component/service/directive/pipe/class
  isMockWindow = false;
  errorTrack: any;

  @ViewChild('twoFactorControl', { read: ElementRef })
  twoFactorEl!: ElementRef;

  activatedRouterPath = '';

  constructor(
    private changeDetectionRef: ChangeDetectorRef,
    private configService: ConfigurationService,
    private queryParamService: QueryParamService,
    private renderer: Renderer2,
    private windowRef: WindowRef,
    private gdlService: GdlService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private optimService: OptimService
  ) {
    this.isMock = this.configService.select<boolean>('env.mock') as boolean;
  }

  ngOnInit(): void {
    this.activatedRouterPath = this.activatedRoute.snapshot.url.join('/');
  }

  ngAfterViewInit(): void {
    this.isMockWindow = this.queryParamService.getFromWindowRef('mock')
      ? true
      : false;
    this.executeDetached(() => {
      this.isLoading = true;
      if (this.isMock || this.isMockWindow) this.__loadMock();
      else this.renderer.setAttribute(this.twoFactorEl.nativeElement, 'src', this.twoFactorPinUrl);
    });

    // If we limit attempts the following would need to be edited to only send the response on final attempt
    this.twoFactorResponse$.pipe(defaultIfEmpty(null)).subscribe((response) => {
      if (response) {
        // user passed PIN validation handleResponse (this ishwere you get response boolean)
        // gdl for submit success
        if (this.router.url === '/contact/edit') {
          this.gdlService.triggerGdlLogEvent('modal', 'confirm', {
            name: 'wpro-contactinfo-2fa-verify',
          });
        } else if (this.router.url === '/login') {
          this.gdlService.triggerGdlLogEvent('modal', 'confirm', {
            name: 'wpro-loginsecurity-2fa-verify',
          });
        }
        // if depending on route params
      } else {
        this.track2faErrors();
      }
    });

    // We are adding this for close tag of two factor iframe
    const closeButton = document.getElementsByClassName(
      'fdx-c-modal__footer__button fdx-c-button fdx-c-button--icon'
    )[0];
    if (closeButton) {
      closeButton.addEventListener('click', this.onCloseButtonCLick.bind(this));
    }
  }

  onCloseButtonCLick(): void {
    if (this.router.url === '/contact/edit') {
      this.gdlService.triggerGdlLogEvent('modal', 'dismiss', {
        name: 'wpro-contactinfo-2fa-verify',
      });
    } else if (this.router.url === '/login') {
      this.gdlService.triggerGdlLogEvent('modal', 'dismiss', {
        name: 'wpro-loginsecurity-2fa-verify',
      });
    }
  }

  ngOnDestroy(): void {
    if (!this.twoFactorResponse$.isStopped) {
      this.twoFactorResponse$.next(false);
      this.twoFactorResponse$.complete();
    }
  }

  /**
   * @internal
   *
   * Solves the 2FA PIN page URL
   */
  get twoFactorPinUrl(): string {
    const host = this.configService.select<string>('env.host');
    const twoFactorPath =
      this.configService.select<string>('external.wlgn.mfa');
    return `https://${host}${twoFactorPath}`;
  }

  /**
   * 2FA authorization: if true user has elevated authority, false otherwise
   */
  isElevatedAuthority$(): Observable<boolean> {
    return this.twoFactorResponse$.asObservable();
  }

  /**
   * @internal
   *
   * Handles WLGN load emission
   *
   * @param details - WLGN window details
   */
  onContainerLoad(details: any): void {
    this.executeDetached(() => {
      const contentHeight = `${(details.height || 300) * 1.01}px`;
      this.renderer.setStyle(
        this.twoFactorEl.nativeElement,
        'height',
        contentHeight
      );
      this.isLoading = false;
    });
  }

  /**
   * @internal
   *
   * Listen for WLGN events
   *
   * @param event - iframe message
   */
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent): void {
    of(event?.data)
      .pipe(
        map(data => JSON.parse(data)),
        filter(json => json?.channel === 'wlgn')
      )
      .subscribe((response) => {
        switch (response?.type) {
          case '2fa':
            this.errorTrack = response?.details;
            this.handle2faEvents(response?.details);
            break;
          case 'window':
            this.onContainerLoad(response?.details);
            break;
          default:
            break;
        }
      });
  }

  /**
   * Handle 2FA events
   */
  handle2faEvents(details: any): void {
    this.twoFactorResponse$.next(details?.status === 'success');
    this.twoFactorResponse$.complete();
  }

  /**
   * gdl tracking for error
   */
  track2faErrors(): void {
    let payload = {};
    let cntxt = '';
    let errorType = '';
    let msg = '';
    const validateApi = 'user/v1/mfa/pin/validate';
    // gdl for error response
    if (
      this.errorTrack.status === 'error' &&
      this.errorTrack.extras[0].code === 'LOGIN.REAUTHENTICATE.ERROR'
    ) {
      errorType = 'server';
      if (this.router.url === '/contact/edit') {
        cntxt = 'wpro-contactinfo-2fa-verify' + ' - LOGIN.REAUTHENTICATE.ERROR';
      } else if (this.router.url === '/login') {
        cntxt = 'wpro-loginsecurity-2fa-verify' + ' - LOGIN.REAUTHENTICATE.ERROR';
      }
      msg = 'Http failure response for ' + this.configService.select<string>('env.host') + validateApi + ' - LOGIN.REAUTHENTICATE.ERROR';
    } else {
      errorType = 'validation';
      msg = 'ELEVATION.FAILED';
      if (this.router.url === '/contact/edit') {
        cntxt = 'wpro-contactinfo-2fa-verify' + '-' + msg;
      } else if (this.router.url === '/login') {
        cntxt = 'wpro-loginsecurity-2fa-verify' + '-' + msg;
      }
    }
    payload = {
      type: errorType,
      source: this.configService.select<string>('env.host') + validateApi,
      message: msg,
      context: cntxt
    };
    this.gdlService.triggerGdlLogEvent('error', 'error', payload);
  }

  /**
   * Run view updates detached to prevent excessive change detections
   *
   * @param func - to be executed detached
   */
  protected executeDetached(func: () => void): void {
    try {
      this.changeDetectionRef.detach();
      func();
    } finally {
      this.changeDetectionRef.reattach();
      this.changeDetectionRef.detectChanges();
    }
  }

  /**
   * Mock spinner-default of the WLGN iframe
   */
  __loadMock(): void {
    // mimic slow load
    setTimeout(() => {
      const container: HTMLElement = this.twoFactorEl.nativeElement;
      const height: number = Math.max(
        container.clientHeight,
        container.scrollHeight
      );
      const windowEvent = { height };
      this.onContainerLoad(windowEvent);
    }, 1500);
  }

  /**
   * Mock the responses from WLGN iframe
   *
   * @param status - was the user's authority elevated
   */
  __onClick(status: string): void {
    const message = JSON.stringify({
      channel: 'wlgn',
      type: '2fa',
      details: { status, extras: '' },
    });
    this.windowRef.nativeWindow.parent.postMessage(
      message,
      '*' /* only do this in development never PROD */
    );
  }
}
